

import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Card, IconButton, Box, List, ListItem, ListItemButton, ListItemText, Tooltip, Alert, Snackbar } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MDTypography from 'components/MDTypography';
import { useAuth } from 'auth/Auth';
import { useSnackbar } from 'notistack';
import { useMaterialUIController } from 'context';
import { useTravelUi } from 'hooks/useTravelUi';
import useUiVersion from 'hooks/useUiVersion';

/** Invoice sections that only belong in layout settings when Travel UI is on. */
const INVOICE_TRAVEL_ONLY_SECTIONS = ['Travel Information'];

/** Account sections that only belong when Travel UI is on (merchant partners). */
const ACCOUNT_TRAVEL_ONLY_SECTIONS = ['Partner Details'];

/** Whole modules that only appear in layout settings when Travel UI is on. */
const TRAVEL_ONLY_MODULES = ['TravelOrder'];

/** Legacy commerce modules hidden from layout settings when Travel UI is on. */
const LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL = ['Quotation', 'Order', 'Product'];

const defaultSections = {
    Lead: [
        'Basic Information',
        'Additional Details',
        'Custom Field Information',
        'Audit Information',
    ],
    Account: [
        'Basic Information',
        'Partner Details',
        'Financial Information',
        'Billing Address',
        'Shipping Address',
        'Custom Fields',
        'Audit Information',
    ],
    Contact: [
        'Basic Information',
        'Account Information',
        'Address',
        'Custom Field Information',
        'Audit Information',
    ],
    Deal: [
        'Deal Information',
        'Related Information',
        'Custom Field Information',
        'Audit Information',
    ],
    Quotation: [
        'Quotation Information',
        'Custom Field Information',
        'Quotation Items',
        'Audit Information',
    ],
    Order: [
        'Order Information',
        'Shipping & Payment',
        'Transport Details',
        'Custom Field Information',
        'Order Items',
        'Audit Information',
    ],
    TravelOrder: [
        'Booking Information',
        'Primary Traveler Information',
        'Trip Package Information',
        'Travelers Information',
        'Add-on Services & Pricing Details',
        'Merchant Order Lines',
        'Audit Information',
        'Booking Timeline',
    ],
    Product: [
        'Product Information',
        'Pricing Information',
        'Inventory Information',
        'Custom Field Information',
        'Audit Information',
    ],
    Invoice: [
        'Invoice Information',
        'Travel Information',
        'Shipping Details',
        'Invoice Items',
        'Payment Schedule',
        'Audit Information',
    ],
    Case: [
        'Basic Information',
        'Related Information',
        'Comments',
        'Audit Information',
    ],
};

// hideLegacyCommerce: travel on in classic. v2 keeps Order / Quotation / Product
// (the Sales app serves them), so it passes false.
const sectionsAvailableForModule = (mod, showTravel, hideLegacyCommerce = showTravel) => {
    if (TRAVEL_ONLY_MODULES.includes(mod) && !showTravel) return [];
    if (LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.includes(mod) && hideLegacyCommerce) return [];
    const all = defaultSections[mod] || [];
    if (mod === 'Invoice' && !showTravel) {
        return all.filter((section) => !INVOICE_TRAVEL_ONLY_SECTIONS.includes(section));
    }
    if (mod === 'Account' && !showTravel) {
        return all.filter((section) => !ACCOUNT_TRAVEL_ONLY_SECTIONS.includes(section));
    }
    return all;
};

/** Keep travel-only invoice sections in saved prefs when Travel is off so order survives toggle. */
const mergePreservedTravelSections = (mod, nextOrder, nextHidden, previous) => {
    if (mod !== 'Invoice') {
        return { order: nextOrder, hidden: nextHidden };
    }
    const prevOrder = previous?.order || [];
    const prevHidden = previous?.hidden || [];
    let order = [...nextOrder];
    INVOICE_TRAVEL_ONLY_SECTIONS.forEach((section) => {
        if (order.includes(section)) return;
        if (!prevOrder.includes(section) && !defaultSections.Invoice.includes(section)) return;
        const prevIdx = prevOrder.indexOf(section);
        const auditIdx = order.indexOf('Audit Information');
        const insertAt = prevIdx >= 0
            ? Math.min(prevIdx, order.length)
            : (auditIdx >= 0 ? auditIdx : order.length);
        order.splice(insertAt, 0, section);
    });
    const hidden = [...nextHidden];
    INVOICE_TRAVEL_ONLY_SECTIONS.forEach((section) => {
        if (prevHidden.includes(section) && !hidden.includes(section) && order.includes(section)) {
            hidden.push(section);
        }
    });
    return { order, hidden };
};
const moduleList = Object.keys(defaultSections);
const moduleLabels = {
    Lead: 'Leads',
    Account: 'Accounts',
    Contact: 'Contacts',
    Deal: 'Deals',
    Quotation: 'Quotations',
    Order: 'Orders',
    TravelOrder: 'Travel Orders',
    Product: 'Products',
    Invoice: 'Invoices',
    Case: 'Cases',
};

function PagelayoutSetting() {
    const { axiosInstance, user } = useAuth();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { showTravel, loading: travelUiLoading } = useTravelUi();
    const { isV2 } = useUiVersion();
    const hideLegacyCommerce = showTravel && !isV2;
    const [selectedModule, setSelectedModule] = useState('Lead');
    const [sectionState, setSectionState] = useState(() => {
        const state = {};
        for (const mod of moduleList) {
            state[mod] = {
                order: defaultSections[mod],
                hidden: [],
            };
        }
        return state;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    // Check if user is admin
    const isAdmin = user?.role === 'SYSTEM_ADMIN' || user?.role === 'system_admin';

    const visibleModules = useMemo(
        () =>
            moduleList.filter((mod) => {
                if (!showTravel && TRAVEL_ONLY_MODULES.includes(mod)) return false;
                if (hideLegacyCommerce && LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.includes(mod)) return false;
                return true;
            }),
        [showTravel, hideLegacyCommerce],
    );

    const visibleOrderForSelected = useMemo(() => {
        const order = sectionState[selectedModule]?.order || [];
        if (TRAVEL_ONLY_MODULES.includes(selectedModule) && !showTravel) return [];
        if (LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.includes(selectedModule) && hideLegacyCommerce) return [];
        if (selectedModule === 'Invoice' && !showTravel) {
            return order.filter((section) => !INVOICE_TRAVEL_ONLY_SECTIONS.includes(section));
        }
        if (selectedModule === 'Account' && !showTravel) {
            return order.filter((section) => !ACCOUNT_TRAVEL_ONLY_SECTIONS.includes(section));
        }
        return order;
    }, [sectionState, selectedModule, showTravel, hideLegacyCommerce]);

    // Swap away from modules that are hidden for the current travel toggle.
    useEffect(() => {
        if (travelUiLoading) return undefined;
        if (!showTravel && TRAVEL_ONLY_MODULES.includes(selectedModule)) {
            setSelectedModule('Order');
        } else if (hideLegacyCommerce && LEGACY_CRM_MODULES_HIDDEN_WHEN_TRAVEL.includes(selectedModule)) {
            setSelectedModule('TravelOrder');
        }
        return undefined;
    }, [travelUiLoading, showTravel, hideLegacyCommerce, selectedModule]);

    // Fetch global preferences on mount (and when travel UI becomes known)
    useEffect(() => {
        if (travelUiLoading) return undefined;

        const fetchGlobalPreferences = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const res = await axiosInstance.get('/preferences/global');
                if (res.data.section_preferences) {
                    // Merge with defaults to ensure all modules are present
                    const merged = { ...sectionState };
                    for (const mod of moduleList) {
                        const moduleKey = mod.toLowerCase();
                        // Full catalogue (includes travel-only invoice sections) for storage.
                        const catalogue = defaultSections[mod];
                        // What admins can rearrange right now (travel gate for Travel Information / Travel Order).
                        const availableSections = sectionsAvailableForModule(mod, showTravel, hideLegacyCommerce);
                        if (res.data.section_preferences[moduleKey]) {
                            // Filter out sections that are no longer available in the code
                            const storedOrder = res.data.section_preferences[moduleKey].order || catalogue;
                            const storedHidden = res.data.section_preferences[moduleKey].hidden || [];
                            
                            // Keep valid stored sections and append any new sections added in code.
                            // Migrate renamed invoice section: Transport Details → Shipping Details
                            const renameSection = (section) =>
                                mod === 'Invoice' && section === 'Transport Details' ? 'Shipping Details' : section;
                            const normalizedOrder = storedOrder.map(renameSection);
                            const normalizedHidden = storedHidden.map(renameSection);
                            // Keep travel-only sections/modules in stored state even when Travel is off.
                            const filteredOrder = normalizedOrder.filter(
                                (section) =>
                                    catalogue.includes(section)
                                    || (mod === 'Invoice' && INVOICE_TRAVEL_ONLY_SECTIONS.includes(section)),
                            );
                            const missingSections = availableSections.filter(
                                (section) => !filteredOrder.includes(section),
                            );
                            const mergedOrder = [...filteredOrder, ...missingSections];
                            const filteredHidden = normalizedHidden.filter((section) => catalogue.includes(section));
                            
                            merged[mod] = {
                                order: mergedOrder.length > 0 ? mergedOrder : catalogue,
                                hidden: filteredHidden,
                            };
                        } else if ((mod === 'Invoice' || TRAVEL_ONLY_MODULES.includes(mod)) && showTravel) {
                            // No stored prefs: seed travel modules / Travel Information for travel tenants.
                            merged[mod] = {
                                order: catalogue,
                                hidden: [],
                            };
                        }
                    }
                    setSectionState(merged);
                    
                    // If any sections were filtered out, update the database
                    const needsUpdate = Object.keys(merged).some((mod) => {
                        const moduleKey = mod.toLowerCase();
                        if (res.data.section_preferences[moduleKey]) {
                            const storedOrder = res.data.section_preferences[moduleKey].order || defaultSections[mod];
                            const availableSections = sectionsAvailableForModule(mod, showTravel, hideLegacyCommerce);
                            const renameSection = (section) =>
                                mod === 'Invoice' && section === 'Transport Details' ? 'Shipping Details' : section;
                            const normalizedStored = storedOrder.map(renameSection);
                            const missingSections = availableSections.filter(
                                (section) => !normalizedStored.includes(section),
                            );
                            return missingSections.length > 0;
                        }
                        return (mod === 'Invoice' || TRAVEL_ONLY_MODULES.includes(mod)) && showTravel;
                    });
                    
                    if (needsUpdate) {
                        await syncDatabaseWithCode(merged);
                    }
                }
            } catch (err) {
                console.error('Error fetching global preferences:', err);
                setError('Failed to load global preferences. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchGlobalPreferences();
        // eslint-disable-next-line
    }, [travelUiLoading, showTravel, hideLegacyCommerce]);

    // Sync database with current code sections
    const syncDatabaseWithCode = async (newSectionState) => {
        if (!isAdmin) {
            return;
        }

        try {
            // Prepare payload in lower-case keys
            const payload = {};
            for (const m of moduleList) {
                payload[m.toLowerCase()] = newSectionState[m];
            }
            
            await axiosInstance.patch('/preferences/global', {
                section_preferences: payload
            });
            
            console.log('Database synced with current code sections');
        } catch (err) {
            console.error('Error syncing database with code:', err);
        }
    };

    // Update global preferences
    const updateGlobalPreferences = async (mod, newOrder, newHidden) => {
        if (!isAdmin) {
            setError('Only administrators can modify global preferences.');
            return;
        }

        try {
            setError(null);
            const preserved = showTravel
                ? { order: newOrder, hidden: newHidden }
                : mergePreservedTravelSections(mod, newOrder, newHidden, sectionState[mod]);
            const newSectionState = {
                ...sectionState,
                [mod]: preserved,
            };
            setSectionState(newSectionState);
            
            // Prepare payload in lower-case keys
            const payload = {};
            for (const m of moduleList) {
                payload[m.toLowerCase()] = newSectionState[m];
            }
            
            await axiosInstance.patch('/preferences/global', {
                section_preferences: payload
            });

            enqueueSnackbar(`Global preferences for ${moduleLabels[mod]} updated successfully!`, { variant: 'success' });
        } catch (err) {
            console.error('Error updating global preferences:', err);
            setError('Failed to update global preferences. Please try again.');
            // Revert state on error
            setSectionState(prev => ({
                ...prev,
                [mod]: sectionState[mod]
            }));
        }
    };

    // Drag and drop handler
    const onDragEnd = (result) => {
        if (!result.destination) return;
        const mod = selectedModule;
        // Rebuild full order from the travel-filtered visible list so hidden
        // travel-only sections stay in storage when Travel is off.
        const visible = visibleOrderForSelected;
        const newVisible = Array.from(visible);
        const [removed] = newVisible.splice(result.source.index, 1);
        newVisible.splice(result.destination.index, 0, removed);
        if (mod === 'Invoice' && !showTravel) {
            const preserved = sectionState[mod].order.filter((s) =>
                INVOICE_TRAVEL_ONLY_SECTIONS.includes(s),
            );
            const auditIdx = newVisible.indexOf('Audit Information');
            const merged = [...newVisible];
            preserved.forEach((section) => {
                const insertAt = auditIdx >= 0 ? merged.indexOf('Audit Information') : merged.length;
                merged.splice(insertAt >= 0 ? insertAt : merged.length, 0, section);
            });
            updateGlobalPreferences(mod, merged, sectionState[mod].hidden);
            return;
        }
        updateGlobalPreferences(mod, newVisible, sectionState[mod].hidden);
    };

    // Toggle visibility
    const toggleVisibility = (section) => {
        const mod = selectedModule;
        let newHidden;
        if (sectionState[mod].hidden.includes(section)) {
            newHidden = sectionState[mod].hidden.filter(s => s !== section);
        } else {
            newHidden = [...sectionState[mod].hidden, section];
        }
        updateGlobalPreferences(mod, sectionState[mod].order, newHidden);
    };

    if (!isAdmin) {
        return (
            <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Only administrators can modify global page layout preferences. 
                    You can view the current settings below.
                </Alert>
                {/* Continue with read-only view */}
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <MDTypography className="pearl-dialog-dup-title" variant="h6" mb={2} fontWeight="medium" color="info">
                Page Layout Settings
            </MDTypography>
            
            <MDTypography variant="body2" mb={3} color="text.secondary">
                Configure the section order and visibility for all users. Changes will apply to everyone in the system.
            </MDTypography>

            <Grid container spacing={2}>
                {/* Sidebar: Modules */}
                <Grid item xs={12} md={3}>
                    <List>
                        {visibleModules.map((mod) => (
                            <ListItem
                                key={mod}
                                disablePadding
                                selected={selectedModule === mod}
                                sx={(theme) => ({
                                    borderRadius: '6px',
                                    mb: 0.5,
                                    py: 0.75,
                                    backgroundColor: selectedModule === mod
                                      ? (darkMode ? 'rgba(168, 200, 255, 0.20)' : 'rgba(21, 101, 200, 0.10)')
                                      : 'transparent',
                                    color: selectedModule === mod
                                      ? (darkMode ? '#A8C8FF' : theme.palette.info.main)
                                      : theme.palette.text.primary,
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(168, 200, 255, 0.14)' : 'rgba(21, 101, 200, 0.08)',
                                    },
                                })}
                            >
                                <ListItemButton onClick={() => setSelectedModule(mod)}>
                                    <ListItemText
                                        primary={moduleLabels[mod]}
                                        primaryTypographyProps={{
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
                {/* Main Content: Sections for selected module */}
                <Grid item xs={12} md={9}>
                    <MDTypography variant="h6" mb={2} fontWeight="medium">
                        {moduleLabels[selectedModule]} Sections
                    </MDTypography>
                    
                    {isLoading || travelUiLoading ? (
                        <MDTypography variant="body2" color="text.secondary">
                            Loading global preferences...
                        </MDTypography>
                    ) : (
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="sections-droppable">
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {visibleOrderForSelected.map((section, idx) => (
                                            <Draggable key={section} draggableId={section} index={idx}>
                                                {(provided) => (
                                                    <Card
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={(theme) => ({
                                                            mb: 1,
                                                            px: 2,
                                                            py: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            minHeight: '56px',
                                                            border: '1px solid',
                                                            borderColor: theme.palette.divider,
                                                            borderRadius: '8px',
                                                            backgroundColor: theme.palette.background.paper,
                                                            '&:hover': {
                                                                backgroundColor: darkMode ? 'rgba(168, 200, 255, 0.06)' : 'rgba(21, 101, 200, 0.03)',
                                                                borderColor: darkMode ? 'rgba(168, 200, 255, 0.35)' : 'rgba(21, 101, 200, 0.30)',
                                                                boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                                                            }
                                                        })}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            <Tooltip title="Drag to reorder" arrow>
                                                                <Box
                                                                    {...provided.dragHandleProps}
                                                                    sx={(theme) => ({
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        cursor: isAdmin ? 'grab' : 'default',
                                                                        color: theme.palette.text.secondary,
                                                                        fontSize: '18px',
                                                                        userSelect: 'none',
                                                                        borderRadius: '4px',
                                                                        '&:hover': {
                                                                            backgroundColor: isAdmin ? (darkMode ? 'rgba(168, 200, 255, 0.16)' : 'rgba(21, 101, 200, 0.10)') : 'transparent',
                                                                        },
                                                                        '&:active': {
                                                                            cursor: isAdmin ? 'grabbing' : 'default',
                                                                        }
                                                                    })}
                                                                >
                                                                    ☰
                                                                </Box>
                                                            </Tooltip>
                                                            <MDTypography
                                                                variant="body1"
                                                                sx={(theme) => ({
                                                                    flex: 1,
                                                                    fontWeight: '500',
                                                                    color: theme.palette.text.primary,
                                                                    ml: 2,
                                                                    fontSize: '14px'
                                                                })}
                                                            >
                                                                {section}
                                                            </MDTypography>
                                                            <Tooltip
                                                                title={sectionState[selectedModule].hidden.includes(section) ? 'Show section' : 'Hide section'}
                                                                arrow
                                                            >
                                                                <IconButton
                                                                    onClick={() => toggleVisibility(section)}
                                                                    size="small"
                                                                    disabled={!isAdmin}
                                                                    sx={(theme) => ({
                                                                        ml: 2,
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        color: sectionState[selectedModule].hidden.includes(section) ? theme.palette.text.secondary : theme.palette.info.main,
                                                                        '&:hover': {
                                                                            backgroundColor: sectionState[selectedModule].hidden.includes(section)
                                                                              ? (darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')
                                                                              : (darkMode ? 'rgba(168, 200, 255, 0.12)' : 'rgba(21, 101, 200, 0.10)'),
                                                                        },
                                                                        '&:disabled': {
                                                                            opacity: 0.5,
                                                                            cursor: 'not-allowed'
                                                                        }
                                                                    })}
                                                                >
                                                                    {sectionState[selectedModule].hidden.includes(section) ?
                                                                        <VisibilityOffIcon fontSize="small" /> :
                                                                        <VisibilityIcon fontSize="small" />
                                                                    }
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Card>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default PagelayoutSetting;